/**
 * Central Connectivity Service (Singleton)
 *
 * Single source of truth for online/offline status.
 * Runs a background ping loop against the Supabase REST endpoint
 * to proactively detect "Lie-Fi" (browser says online but server unreachable).
 *
 * Usage:
 *   import { connectivityService } from '@/lib/connectivityService';
 *   connectivityService.start();                    // once at app boot
 *   const online = connectivityService.isOnline;    // synchronous read
 *   const unsub = connectivityService.subscribe(cb); // React integration
 */

import { logger } from "@/lib/logger";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

const PING_TIMEOUT_MS = 3_000;
const ONLINE_INTERVAL_MS = 15_000;
const OFFLINE_INTERVAL_MS = 10_000;
const WAS_OFFLINE_RESET_MS = 5_000;

type Listener = () => void;

class ConnectivityService {
  // eslint-disable-next-line no-restricted-syntax -- this IS the connectivity service that wraps navigator.onLine
  private _isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
  private _wasOffline = false;
  private _listeners = new Set<Listener>();
  private _intervalId: ReturnType<typeof setInterval> | null = null;
  private _wasOfflineTimer: ReturnType<typeof setTimeout> | null = null;
  private _started = false;

  // ---- Public API ----

  get isOnline(): boolean {
    return this._isOnline;
  }

  get wasOffline(): boolean {
    return this._wasOffline;
  }

  /** Subscribe to status changes. Returns unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  /** One-shot check. Also updates internal state. */
  async checkNow(): Promise<boolean> {
    const reachable = await this._ping();
    this._setOnline(reachable);
    return reachable;
  }

  /** Start background ping loop + browser event listeners. Idempotent. */
  start(): void {
    if (this._started) return;
    this._started = true;

    window.addEventListener("online", this._handleBrowserOnline);
    window.addEventListener("offline", this._handleBrowserOffline);

    // If browser already says offline at start, verify
    // eslint-disable-next-line no-restricted-syntax -- this IS the connectivity service that wraps navigator.onLine
    if (!navigator.onLine) {
      this.checkNow();
    }

    this._scheduleInterval();
  }

  /** Stop everything. Mainly for tests. */
  stop(): void {
    this._started = false;
    window.removeEventListener("online", this._handleBrowserOnline);
    window.removeEventListener("offline", this._handleBrowserOffline);
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    if (this._wasOfflineTimer) {
      clearTimeout(this._wasOfflineTimer);
      this._wasOfflineTimer = null;
    }
  }

  // ---- Internal ----

  private _notify(): void {
    for (const listener of this._listeners) {
      try {
        listener();
      } catch {
        // swallow subscriber errors
      }
    }
  }

  private _setOnline(online: boolean): void {
    const prev = this._isOnline;
    this._isOnline = online;

    if (!prev && online) {
      // Transition offline → online
      this._wasOffline = true;
      if (this._wasOfflineTimer) clearTimeout(this._wasOfflineTimer);
      this._wasOfflineTimer = setTimeout(() => {
        this._wasOffline = false;
        this._notify();
      }, WAS_OFFLINE_RESET_MS);
    }

    if (prev !== online) {
      logger.info("Connectivity transition", {
        from: prev ? "online" : "offline",
        to: online ? "online" : "offline",
      });
      this._scheduleInterval(); // switch interval cadence
      this._notify();
    }
  }

  private _scheduleInterval(): void {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    if (!this._started) return;

    const interval = this._isOnline ? ONLINE_INTERVAL_MS : OFFLINE_INTERVAL_MS;
    logger.debug("Connectivity ping interval set", {
      mode: this._isOnline ? "online" : "offline",
      intervalMs: interval,
    });
    this._intervalId = setInterval(() => {
      this.checkNow();
    }, interval);
  }

  private async _ping(): Promise<boolean> {
    if (!SUPABASE_URL) return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    try {
      await fetch(`${SUPABASE_URL}/auth/v1/health`, {
        method: "HEAD",
        signal: controller.signal,
      });
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  private _handleBrowserOnline = (): void => {
    // Browser says online → verify with an immediate ping
    this.checkNow();
  };

  private _handleBrowserOffline = (): void => {
    // Browser offline event is reliable → set immediately
    this._setOnline(false);
  };
}

/** Singleton instance – import this everywhere. */
export const connectivityService = new ConnectivityService();
